// Portuguese (Portugal, formal) copy for /preview-first-uploads. Same shape as copy.en.ts (checked by copy.test.ts).
// Matches /web-uploader and /on-demand-ftp: "o envio web", "pré-visualizações", "originais" (never "qualidade
// total"), "a sua galeria", "ligação", "descarregar". The uploader's own labels are in English, so they're quoted
// as it shows them. "Fotos", not "fotografias" (copy.test.ts keeps every role word out).
import type { PreviewFirstCopy } from "./copy.en";

const pt: PreviewFirstCopy = {
  seo: {
    title: "Envio com pré-visualizações, em todos os planos | Photohawk",
    description:
      "Novidade no envio web da Photohawk, em todos os planos: uma pequena pré-visualização de cada foto é carregada primeiro, para que a sua galeria possa ficar online, vender e ser partilhada em minutos, enquanto os originais seguem em segundo plano.",
  },
  hero: {
    badge: "Novo",
    eyebrow: "No envio web · Todos os planos",
    title: "A sua galeria online em minutos,",
    titleHighlight: "e não em horas",
    subtitle:
      "O envio web passa a enviar primeiro uma pequena pré-visualização de cada foto. A sua galeria pode ficar online com essas pré-visualizações, para que os compradores encontrem, comprem e partilhem as suas fotos enquanto os originais continuam a ser carregados em segundo plano.",
    ctaPrimary: "Abrir o envio web",
    ctaSecondary: "Ver como funciona",
    /** Back to the web uploader launch page (/web-uploader). */
    uploaderLink: "Veja todas as novidades do envio web",
  },
  steps: {
    heading: "Como funciona",
    subtitle: "As pré-visualizações primeiro estão no envio web, em todos os planos. Ao adicionar fotos, vê quanto demora o envio de cada forma e escolhe «Previews first, originals later».",
    stepLabel: "Passo {n}",
    items: [
      {
        id: "add",
        title: "Adicione as suas fotos",
        body: "Arraste fotos ou uma pasta inteira, como já faz hoje. O seu navegador cria uma pequena pré-visualização de cada uma, no seu próprio computador.",
      },
      {
        id: "previews",
        title: "As pré-visualizações são carregadas primeiro",
        body: "As pré-visualizações são pequenas, por isso carregam depressa, mesmo com uma ligação lenta no local do evento.",
      },
      {
        id: "live",
        title: "A sua galeria fica online",
        body: "Publique assim que as pré-visualizações estiverem carregadas, ou marque «Publish the gallery as soon as the previews are up» para que isso aconteça sozinho. Os compradores podem navegar, pesquisar, comprar e partilhar de imediato.",
      },
      {
        id: "full",
        title: "Seguem-se os originais",
        body: "Os originais continuam a ser carregados em segundo plano, nas mesmas fotos. Os downloads e as impressões usam sempre os originais.",
      },
    ],
    timeline: {
      label: "Um envio de exemplo, do início ao fim",
      previews: "Pré-visualizações",
      previewsTime: "minutos",
      live: "Galeria online",
      full: "Originais",
      fullTime: "em segundo plano",
    },
  },
  buyers: {
    heading: "O que os compradores veem",
    subtitle: "Nada de novo para aprender. Os compradores veem a galeria de sempre, só que mais cedo.",
    items: [
      {
        id: "protected",
        title: "Com marca de água e protegidas, como sempre",
        body: "Os compradores veem as mesmas pré-visualizações com marca de água de sempre, e a proteção das pré-visualizações funciona da mesma forma se a sua galeria a utilizar.",
      },
      {
        id: "search",
        title: "Encontram-se de imediato",
        body: "A pesquisa por selfie e por dorsal funciona com as pré-visualizações, por isso as pessoas encontram as suas fotos assim que a galeria fica online.",
      },
      {
        id: "delivery",
        title: "Originais, entregues automaticamente",
        body: "Uma compra ou um download gratuito nunca é entregue como pré-visualização. Se alguém comprar ou descarregar uma foto antes de o original chegar, esse original passa para a frente da fila, e a pessoa recebe-o assim que estiver pronto.",
      },
    ],
  },
  strip: {
    heading: "Acompanhe enquanto carrega",
    body: "Enquanto as pré-visualizações e os originais são carregados, uma faixa no topo do envio web mostra o estado da sua galeria: se já está online, quantos originais ainda faltam e quando uma foto comprada passou para a frente. Quando a galeria já está online, mostra também quantas pessoas a visitaram nos últimos 30 minutos, e os proprietários e administradores do espaço de trabalho veem as novas encomendas desde o início do envio.",
    exampleLabel: "Exemplo",
    live: "Online com as pré-visualizações",
    orders: "3 encomendas desde que começou",
    visitors: "42 visitantes nos últimos 30 min",
    left: "Faltam 1250 originais",
    jumped: "Os originais de 2 fotos encomendadas passaram para a frente",
  },
  faq: {
    heading: "Perguntas",
    items: [
      {
        q: "Tenho de manter o envio web aberto?",
        a: "É preferível, até os originais terminarem; a faixa mostra quantos faltam. Se o fechar antes, não se perde nada. A sua galeria continua online com as pré-visualizações e, da próxima vez que abrir essa galeria no envio web, este indica quantas fotos ainda estão à espera. Adicione novamente a mesma pasta (mesmo mais tarde, a partir de casa) e só são enviados os originais em falta.",
      },
      {
        q: "E se alguém comprar uma foto antes de o original chegar?",
        a: "Pode comprá-la na mesma. Enquanto o envio web estiver a correr, o original dessa foto passa para a frente da fila. O e-mail da encomenda espera pelo ficheiro e é enviado poucos minutos depois de este chegar, por isso o comprador nunca recebe a pré-visualização por engano. Se o envio web tiver sido fechado, a encomenda aguarda até voltar a adicionar a pasta. O envio web indica-lhe as fotos pelas quais um comprador está à espera, e essas são enviadas primeiro.",
      },
      {
        q: "Funciona em galerias de download gratuito?",
        a: "Sim. As pessoas podem ver, pesquisar e descarregar assim que as pré-visualizações estiverem carregadas, e um download nunca é a pré-visualização. Se alguém descarregar uma foto antes de o original chegar, esse original passa para a frente da fila: o download indica que o original ainda está a ser carregado e passa a funcionar assim que o ficheiro chega, e um download enviado por e-mail segue nesse momento. Cada download conta uma única vez.",
      },
      {
        q: "A pesquisa por selfie e por dorsal funciona com as pré-visualizações?",
        a: "Sim. Os rostos e os números de dorsal são detetados nas pré-visualizações, por isso a pesquisa funciona assim que estas estiverem carregadas. Enquanto o envio web estiver aberto, envia também grandes planos mais nítidos retirados dos seus originais para ajudar a pesquisa.",
      },
      {
        q: "Que planos incluem esta funcionalidade?",
        a: "Todos os planos, incluindo o Lite. Se o seu espaço de trabalho já utilizou as pré-visualizações primeiro, essas fotos terminam normalmente: adicione novamente a mesma pasta e os respetivos originais são enviados, e as encomendas e os downloads continuam a chegar.",
      },
      {
        q: "Tem algum custo adicional?",
        a: "Não. Está incluída em todos os planos, e cada foto conta uma única vez: a pré-visualização e o respetivo original são a mesma foto. A única diferença é que a sua ligação envia um pouco mais de dados no total (cerca de 5% a mais).",
      },
      {
        q: "Funciona com FTP e com o Photohawk Studio?",
        a: "Por enquanto, não. As pré-visualizações primeiro fazem parte do envio web. As fotos enviadas por FTP ou a partir do Photohawk Studio são carregadas como originais de uma só vez, como antes.",
      },
      {
        q: "Está ativa por predefinição?",
        a: "Não. Sempre que adiciona fotos, o envio web mostra quanto tempo vão demorar com e sem pré-visualizações primeiro, e enviar os originais de imediato fica selecionado. Escolha «Previews first, originals later» quando quiser a galeria online mais cedo; o envio web lembra-se da sua escolha para a próxima vez. Com uma resolução de envio menor («Large» ou «Web») só são carregados ficheiros redimensionados, por isso as pré-visualizações primeiro não se aplicam.",
      },
    ],
  },
  cta: {
    heading: "Experimente no seu próximo envio",
    body: "As pré-visualizações primeiro estão no envio web, em todos os planos. Adicione as suas fotos, escolha as pré-visualizações primeiro e a sua galeria pode estar online em minutos.",
    button: "Abrir o envio web",
  },
};

export default pt;
